'use client';

import React, { useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { getTranslations } from '@/lib/i18n';

export default function DownloadSection() {
  const { language } = useLanguage();
  const t = getTranslations(language).download;

  const [phone, setPhone] = useState('');
  const [sent, setSent] = useState(false);

  const handleSend = (e: React.FormEvent) => {
    e.preventDefault();
    if (phone.trim()) {
      setSent(true);
      setTimeout(() => setSent(false), 3000);
      setPhone('');
    }
  };

  return (
    <section id="download" className="section-glow py-32 px-6 relative overflow-hidden">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] rounded-full bg-dispatch-cyan/5 blur-[120px] pointer-events-none" />

      <div className="max-w-4xl mx-auto relative z-10 text-center">
        <div className="inline-flex items-center gap-2 mb-6">
          <span className="w-2 h-2 rounded-full bg-dispatch-cyan badge-pulse" />
          <span className="text-dispatch-cyan font-mono text-xs tracking-widest uppercase">{t.badge}</span>
        </div>

        <h2 className="font-manrope font-extrabold leading-[0.9] tracking-tighter mb-6"
          style={{ fontSize: 'clamp(2.8rem, 8vw, 5rem)' }}>
          <span className="text-dispatch-text">{t.headline1}</span><br />
          <span className="text-dispatch-cyan">{t.headline2}</span>
        </h2>

        <p className="text-dispatch-muted text-xl font-dm-sans leading-relaxed max-w-2xl mx-auto mb-12">
          {t.subtitle}
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
          <a href="#" className="group flex items-center gap-4 px-8 py-5 bg-dispatch-cyan text-dispatch-bg rounded-2xl font-bold font-mono tracking-wide hover:bg-white transition-all duration-300 shadow-cyan-lg hover:shadow-cyan-md w-full sm:w-auto justify-center">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.7 9.05 7.42c1.42.07 2.38.74 3.2.8 1.22-.24 2.39-.93 3.72-.84 1.58.13 2.77.77 3.54 1.96-3.27 1.96-2.49 6.27.54 7.48-.65 1.72-1.49 3.43-3 4.46zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
            </svg>
            <div className="text-left">
              <p className="text-[10px] font-mono opacity-70 leading-none mb-0.5">{t.appStoreLabel}</p>
              <p className="text-base font-manrope font-extrabold leading-none">{t.appStoreName}</p>
            </div>
          </a>

          <a href="#" className="group flex items-center gap-4 px-8 py-5 bg-dispatch-surface border border-dispatch-cyan/30 text-dispatch-text rounded-2xl font-bold font-mono tracking-wide hover:bg-dispatch-surface2 hover:border-dispatch-cyan/50 transition-all duration-300 w-full sm:w-auto justify-center">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className="text-dispatch-cyan">
              <path d="M3.18 23.76c.3.17.65.18.97.04L14.76 12 3.15.2C2.83.06 2.48.07 2.18.24A1.5 1.5 0 001.5 1.5v21c0 .55.28 1.04.68 1.26zm16.57-11.3l-2.7-1.56-3.13 3.13 3.13 3.12 2.71-1.56a1.5 1.5 0 000-3.13zm-15.6 8.9l9.35-9.36-2.76-2.76L1.5 19.1v1.77l2.65 1.49zM3.15.2l9.35 9.36-2.76 2.76L1.5 3.13V1.36L3.15.2z"/>
            </svg>
            <div className="text-left">
              <p className="text-[10px] font-mono opacity-70 leading-none mb-0.5">{t.googlePlayLabel}</p>
              <p className="text-base font-manrope font-extrabold leading-none">{t.googlePlayName}</p>
            </div>
          </a>
        </div>

        <div className="flex items-center gap-4 max-w-sm mx-auto mb-8">
          <div className="flex-1 h-px bg-dispatch-surface" />
          <span className="text-dispatch-faint font-mono text-xs uppercase tracking-widest">{t.or}</span>
          <div className="flex-1 h-px bg-dispatch-surface" />
        </div>

        <div className="max-w-md mx-auto">
          <p className="text-dispatch-muted font-mono text-sm mb-4 uppercase tracking-widest">{t.smsLabel}</p>
          <form onSubmit={handleSend} className="flex gap-3">
            <div className="flex-1 relative">
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder={t.smsPlaceholder}
                className="w-full bg-dispatch-surface border border-dispatch-surface2 text-dispatch-text font-mono text-sm px-4 py-3.5 rounded-xl placeholder-dispatch-faint focus:outline-none focus:border-dispatch-cyan/60 transition-colors"
              />
            </div>
            <button
              type="submit"
              className={`px-6 py-3.5 rounded-xl font-bold font-mono text-sm transition-all duration-300 shrink-0 ${
                sent
                  ? 'bg-green-500/20 border border-green-500/40 text-green-400' :'bg-dispatch-cyan text-dispatch-bg hover:bg-white shadow-cyan-sm'
              }`}
            >
              {sent ? t.smsSent : t.smsSend}
            </button>
          </form>
          <p className="text-dispatch-faint font-mono text-[10px] mt-3 uppercase tracking-widest">
            {t.smsDisclaimer}
          </p>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-6 mt-12">
          {t.trust.map((item) => (
            <div key={item.text} className="flex items-center gap-2 text-dispatch-faint font-mono text-xs">
              <span>{item.icon}</span>
              <span>{item.text}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}